import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import Loader from "../../common/Loader";
import DetailsModal from "../../common/DetailsModal";
import {
  getMerchants,
  getMerchantById,
} from "../../../services/merchantService";
import useAdminAccountDeletion from "../../common/useAdminAccountDeletion";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import CancelPopup from "../../common/CancelPopup";
import { ReactComponent as DeleteIcon } from "../../../Assets/Icons/deleteIcon.svg";
import SearchBar from "../../common/SearchBar";
import debounce from "lodash.debounce";
import Confirmation from "../../common/OfferList/Confirmation";
import AddIcon from "../../../Assets/Icons/AddIcon.svg";
import PopupContent from "../MerchantList/AdminMerchantDetailsPopupContent";
import EditMerchantModal from "../MerchantList/EditMerchantModal";

const InProcessMerchantList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((state) => state?.merchantReducer?.loading);

  const [allMerchants, setAllMerchants] = useState();
  const [fullMerchantsList, setFullMerchantsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [merchantDetails, setMerchantDetails] = useState(null);
  const [isMerchantDetailOpen, setIsMerchantDetailOpen] = useState(false);
  const { openDeletion, modal: accountDeletionModal } =
    useAdminAccountDeletion();
  const [showEditMerchantModal, setShowEditMerchantModal] = useState(false);
  const [merchantForEdit, setMerchantForEdit] = useState(null);

  const items = [
    {
      title: "Merchants",
    },
    {
      title: "In-Progress Merchants",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Name",
    },
    {
      name: "Date joined",
    },
    {
      name: "Email",
    },
    {
      name: "Contact",
    },
    {
      name: "Address",
    },
    {
      name: "Status",
    },
  ];

  const isInProcess = (merchant) => {
    const status = (merchant?.status || "").toLowerCase();
    // A merchant is only considered "In-Process" in the Merchant List if they have actually
    // started their onboarding (has a user account or entered business details).
    // Otherwise, they stay in the Invitation List.
    const hasStarted = !!merchant.userId || !!merchant.user;
    const inProcessStatuses = [
      "pending",
      "accepted",
      "completed",
      "in-process",
      "in-progress",
    ];

    if (status === "pending" || status === "") {
      return hasStarted;
    }

    return inProcessStatuses.includes(status);
  };

  const updatePageSlice = (data, page) => {
    if (!data) return;
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    setAllMerchants(data.slice(startIndex, endIndex));
  };

  const getMerchantsData = async (search) => {
    let searchParams = {
      limit: 1000, // Fetch all for client-side filtering
      // Note: Fetch all to filter client-side as backend might not map "in-process" status directly
      status: null,
      duration: null,
    };

    if (search && search.trim()) {
      const searchTerm = search.trim();
      const isContactNumber = /^[\d\s+\-()]+$/.test(searchTerm);
      const isDate =
        /^[\d/\-]+$/.test(searchTerm) &&
        (searchTerm.includes("/") || searchTerm.includes("-"));

      if (isContactNumber) {
        searchParams.contactSearch = searchTerm;
      } else if (isDate) {
        searchParams.dateSearch = searchTerm;
      } else {
        searchParams.search = searchTerm;
      }
    }

    Object.keys(searchParams).forEach((key) =>
      searchParams[key] === null ? delete searchParams[key] : {},
    );

    const merchantsPromise = await dispatch(
      getMerchants(searchParams),
    )?.unwrap();

    let combinedData = [];
    if (merchantsPromise?.success === true) {
      combinedData = [...(merchantsPromise?.data || [])];
    }

    if (combinedData.length > 0 || merchantsPromise?.success === true) {
      // Helper to check for the internal "Pending_..." placeholder strings
      const isInternalPlaceholder = (val) =>
        typeof val === "string" && val.startsWith("Pending_");

      // Filter to show only in-process merchants and exclude placeholder records
      const inProcessMerchants = combinedData.filter((merchant) => {
        const businessName =
          merchant.businessName || merchant.business_name || "";
        const hasValidBusinessName =
          !!businessName && !isInternalPlaceholder(businessName);
        const hasAccount = !!merchant.userId || !!merchant.user;

        return isInProcess(merchant) && (hasAccount || hasValidBusinessName);
      });

      // SORT BY DATE DESCENDING (Latest on top)
      inProcessMerchants.sort((a, b) => {
        const dateA = new Date(
          a.createdAt || a.created_at || a.notifiedAt || a.updatedAt || 0,
        );
        const dateB = new Date(
          b.createdAt || b.created_at || b.notifiedAt || b.updatedAt || 0,
        );
        return dateB - dateA;
      });

      setFullMerchantsList(inProcessMerchants);

      // Calculate total pages
      const total = Math.ceil((inProcessMerchants?.length || 0) / 10);
      setTotalPages(total || 1);
      setCount(inProcessMerchants?.length || 0);

      const newPage = search || searchQuery ? 1 : currentPage;
      setCurrentPage(newPage);
      updatePageSlice(inProcessMerchants, newPage);
    }
  };

  useEffect(() => {
    getMerchantsData(searchQuery);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (fullMerchantsList.length > 0) {
      updatePageSlice(fullMerchantsList, currentPage);
    }
  }, [currentPage]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of merchant list when changing pages
    const container = document.getElementById("merchant-list-container");
    if (container) {
      container.scrollIntoView({ behavior: "smooth" });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        getMerchantsData(query);
      }, 500),
    [],
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const fetchMerchantDetails = async (merchantId) => {
    try {
      const merchantDetailsPromise = await dispatch(
        getMerchantById({ id: merchantId, silent: true }),
      )?.unwrap();
      if (merchantDetailsPromise?.success === true) {
        setMerchantDetails(merchantDetailsPromise?.data);
        setIsMerchantDetailOpen(true);
      } else {
        console.error(
          "Failed to fetch merchant details:",
          merchantDetailsPromise,
        );
      }
    } catch (error) {
      console.error("Error fetching merchant details:", error);
    }
  };

  const openMerchantPopup = (merchantId) => {
    fetchMerchantDetails(merchantId);
  };

  const closeMerchantPopup = () => {
    setIsMerchantDetailOpen(false);
  };

  const handleOpenEditMerchantModal = (merchant) => {
    setMerchantForEdit(merchant);
    setIsMerchantDetailOpen(false);
    setTimeout(() => {
      setShowEditMerchantModal(true);
    }, 100);
  };

  const handleCloseEditMerchantModal = () => {
    setShowEditMerchantModal(false);
    setMerchantForEdit(null);
    setTimeout(() => {
      setIsMerchantDetailOpen(true);
    }, 100);
  };

  const handleEditMerchantSuccess = () => {
    getMerchantsData(searchQuery);
    if (merchantDetails?._id) {
      fetchMerchantDetails(merchantDetails._id);
    }
  };

  const handleDeleteMerchant = (id) => {
    openDeletion("merchant", id, () => {
        getMerchantsData(searchQuery);
    });
  };

  return (
    <>
      {loading === true && <Loader />}
      {isMerchantDetailOpen && (
        <DetailsModal
          handleClose={closeMerchantPopup}
          content={
            <PopupContent
              merchantDetails={merchantDetails}
              onDelete={handleDeleteMerchant}
            />
          }
          popupClassName="p-6 w-[335px] sm:w-[600px] md:w-[728px]"
        />
      )}
      {showEditMerchantModal && merchantForEdit && (
        <EditMerchantModal
          merchantDetails={merchantForEdit}
          onClose={handleCloseEditMerchantModal}
          onSuccess={handleEditMerchantSuccess}
        />
      )}
      {accountDeletionModal}
      <div className="flex justify-between col-span-12">
        <BreadCrumsHeader heading="In-Progress Merchants" items={items} />
        <div className="flex gap-3 items-center">
          <button
            onClick={() => navigate("/admin/merchant-list")}
            className="flex items-center justify-center bg-white text-[#00ADEF] font-[600] text-[14px] h-[38px] px-4 rounded-[5px] transition-colors duration-150 hover:bg-[#F0F9FF] font-nunito border border-[#00ADEF] whitespace-nowrap"
            style={{
              boxShadow: "0 2px 8px rgba(0,173,239,0.10)",
              letterSpacing: 0.1,
            }}
          >
            Merchant List
          </button>
        </div>
      </div>
      <div className="w-full flex justify-start items-center mt-4">
        <SearchBar
          parentDivClass="w-[65%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
          divClassName="relative w-full"
          inputClassName="searchBar w-full p-3 pr-10 text-[14px] font-poppins font-[400] outline-none rounded-[5px] border border-[#D9D9D9] focus:border-[#00ADEF]"
          iconClassName="absolute right-[10px] top-1/2 -translate-y-1/2 w-[22px] h-[22px]"
          placeholder="Search"
          value={searchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
          onClear={() => handleSearchChange("")}
        />
      </div>
      <div id="merchant-list-container" className="col-span-12 mt-4">
        <TableContainer style={{ width: "100%", overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: "800px",
              borderCollapse: "separate",
              borderSpacing: "0px 8px",
            }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                {columns.map((column) => {
                  return (
                    <TableCell key={column.name} className="!text-[#FFFFFF]">
                      {column.name}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {allMerchants?.map((merchant, index) => {
                // Helper function to get email (handles both invited and regular merchants)
                const getEmail = () =>
                  merchant?.user?.email || merchant?.email || "N/A";

                // Helper function to get date (handles both formats)
                const getDate = () => {
                  const date =
                    merchant?.createdAt ||
                    merchant?.created_at ||
                    merchant?.notifiedAt;
                  if (!date) return "N/A";
                  return (
                    separateDateTimeAndConvertToLocale(date)?.localDate || date
                  );
                };

                return (
                  <TableRow
                    key={merchant._id}
                    className="h-[50px]"
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      boxShadow: "0px 4px 4px 0px #00000040",
                      backgroundColor:
                        (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                    }}
                  >
                    <TableCell className="max-w-[50px]">
                      {(currentPage - 1) * 10 + index + 1}
                    </TableCell>
                    <TableCell
                      title={merchant?.businessName}
                      className="max-w-[120px] truncate"
                    >
                      {merchant?.businessName || "N/A"}
                    </TableCell>
                    <TableCell
                      title={getDate()}
                      className="max-w-[120px] truncate"
                    >
                      {getDate()}
                    </TableCell>
                    <TableCell
                      title={getEmail()}
                      className="max-w-[140px] truncate"
                    >
                      {getEmail()}
                    </TableCell>
                    <TableCell
                      title={merchant?.contactPersonNumber || "N/A"}
                      className="max-w-[120px] truncate"
                    >
                      {merchant?.contactPersonNumber || "N/A"}
                    </TableCell>
                    <TableCell
                      title={merchant?.address || "N/A"}
                      className="max-w-[180px] truncate"
                    >
                      {merchant?.address || "N/A"}
                    </TableCell>
                    <TableCell className="max-w-[110px]">
                      <Confirmation
                        status="in-process"
                        creatorRole="merchant"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      </div>
    </>
  );
};

export default InProcessMerchantList;
